/**
 * Operations Tasks API Functions
 */

import { api } from '@/lib/api';
import type {
  Task,
  TaskListItem,
  TaskComment,
  TaskAuditLog,
  TasksResponse,
  TasksStats,
  TasksQueryParams,
  KanbanQueryParams,
  KanbanData,
  CreateTaskInput,
  UpdateTaskInput,
  UpdatePositionsInput,
  TaskStatus,
  TaskPriority,
  SubtaskListItem,
  V4TasksResponse,
  V4TaskListItem,
  V4Task,
  V4TaskComment,
  V4KanbanResponse,
  V4TasksStatsResponse,
  V4AuditLogsResponse,
  V4SubtaskListItem,
} from './types';
import { TASK_STATUS_LABELS } from './types';

// =============================================================================
// Transform Functions
// =============================================================================

function transformV4SubtaskListItem(v4: V4SubtaskListItem): SubtaskListItem {
  return {
    id: v4.id,
    task_number: v4.taskNumber,
    title: v4.title,
    status: v4.status as TaskStatus,
    due_date: v4.dueDate,
    position: v4.position,
    parent_task_id: v4.parentTaskId,
  };
}

function transformV4TaskListItem(v4: V4TaskListItem): TaskListItem {
  return {
    id: v4.id,
    task_number: v4.taskNumber,
    title: v4.title,
    description: v4.description,
    project_id: v4.projectId,
    quote_id: v4.quoteId,
    project_number: v4.projectNumber,
    project_name: v4.projectName,
    status: v4.status as TaskStatus,
    priority: v4.priority as TaskPriority,
    assigned_to_id: v4.assignedToId,
    assigned_to_name: v4.assignedToName,
    due_date: v4.dueDate,
    estimated_hours: v4.estimatedHours ? parseFloat(v4.estimatedHours) : undefined,
    actual_hours: v4.actualHours ? parseFloat(v4.actualHours) : undefined,
    tags: v4.tags || [],
    position: v4.position,
    comments_count: v4.commentsCount,
    created_at: v4.createdAt,
    subtasks: v4.subtasks?.map(transformV4SubtaskListItem),
  };
}

function transformV4TaskComment(v4: V4TaskComment): TaskComment {
  return {
    id: v4.id,
    task_id: v4.taskId,
    content: v4.content,
    author_id: v4.authorId,
    author_name: v4.authorName,
    author_email: v4.authorEmail,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    deleted_at: v4.deletedAt,
  };
}

function transformV4Task(v4: V4Task): Task {
  return {
    id: v4.id,
    task_number: v4.taskNumber,
    title: v4.title,
    description: v4.description,
    project_id: v4.projectId,
    quote_id: v4.quoteId,
    project_number: v4.projectNumber,
    project_name: v4.projectName,
    milestone_key: v4.milestoneKey,
    status: v4.status as TaskStatus,
    priority: v4.priority as TaskPriority,
    assigned_to_id: v4.assignedToId,
    assigned_to_name: v4.assignedToName,
    assigned_to_email: v4.assignedToEmail,
    created_by_id: v4.createdById,
    created_by_name: v4.createdByName,
    due_date: v4.dueDate,
    start_date: v4.startDate,
    completed_at: v4.completedAt,
    completed_by_id: v4.completedById,
    completed_by_name: v4.completedByName,
    estimated_hours: v4.estimatedHours ? parseFloat(v4.estimatedHours) : undefined,
    actual_hours: v4.actualHours ? parseFloat(v4.actualHours) : undefined,
    tags: v4.tags || [],
    position: v4.position,
    comments: v4.comments?.map(transformV4TaskComment) || [],
    comments_count: v4.commentsCount,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    archived_at: v4.archivedAt,
  };
}

function transformV4KanbanResponse(v4: V4KanbanResponse): KanbanData {
  return {
    columns: v4.columns.map((col) => ({
      status: col.status as TaskStatus,
      label: col.label || TASK_STATUS_LABELS[col.status as TaskStatus] || col.status,
      tasks: col.tasks.map(transformV4TaskListItem),
    })),
    totalCount: v4.totalCount,
  };
}

// Transform input to camelCase for API
function transformCreateInput(input: CreateTaskInput): Record<string, unknown> {
  return {
    title: input.title,
    description: input.description,
    projectId: input.project_id,
    quoteId: input.quote_id,
    milestoneKey: input.milestone_key,
    status: input.status,
    priority: input.priority,
    assignedToId: input.assigned_to_id,
    assignedToName: input.assigned_to_name,
    assignedToEmail: input.assigned_to_email,
    dueDate: input.due_date,
    startDate: input.start_date,
    estimatedHours: input.estimated_hours,
    tags: input.tags,
    position: input.position,
  };
}

function transformUpdateInput(input: UpdateTaskInput): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (input.title !== undefined) result.title = input.title;
  if (input.description !== undefined) result.description = input.description;
  if (input.project_id !== undefined) result.projectId = input.project_id;
  if (input.quote_id !== undefined) result.quoteId = input.quote_id;
  if (input.milestone_key !== undefined) result.milestoneKey = input.milestone_key;
  if (input.status !== undefined) result.status = input.status;
  if (input.priority !== undefined) result.priority = input.priority;
  if (input.assigned_to_id !== undefined) result.assignedToId = input.assigned_to_id;
  if (input.assigned_to_name !== undefined) result.assignedToName = input.assigned_to_name;
  if (input.assigned_to_email !== undefined) result.assignedToEmail = input.assigned_to_email;
  if (input.due_date !== undefined) result.dueDate = input.due_date;
  if (input.start_date !== undefined) result.startDate = input.start_date;
  if (input.estimated_hours !== undefined) result.estimatedHours = input.estimated_hours;
  if (input.actual_hours !== undefined) result.actualHours = input.actual_hours;
  if (input.tags !== undefined) result.tags = input.tags;
  if (input.position !== undefined) result.position = input.position;

  return result;
}

// =============================================================================
// API Functions
// =============================================================================

export async function fetchTasks(params: TasksQueryParams = {}): Promise<TasksResponse> {
  const queryParams: Record<string, string | number | boolean> = {};

  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;
  if (params.sortKey) queryParams.sortKey = params.sortKey;
  if (params.sortDirection) queryParams.sortDirection = params.sortDirection;
  if (params.status) queryParams.status = params.status;
  if (params.statuses?.length) queryParams.statuses = params.statuses.join(',');
  if (params.priority) queryParams.priority = params.priority;
  if (params.projectId) queryParams.projectId = params.projectId;
  if (params.quoteId) queryParams.quoteId = params.quoteId;
  if (params.assignedToId) queryParams.assignedToId = params.assignedToId;
  if (params.dueDatePreset) {
    const range = computeDueDateRange(params.dueDatePreset);
    queryParams.dueDateFrom = range.dueDateFrom;
    queryParams.dueDateTo = range.dueDateTo;
  } else {
    if (params.dueDateFrom) queryParams.dueDateFrom = params.dueDateFrom;
    if (params.dueDateTo) queryParams.dueDateTo = params.dueDateTo;
  }
  if (params.search) queryParams.search = params.search;
  if (params.includeArchived) queryParams.includeArchived = params.includeArchived;
  if (params.tags?.length) queryParams.tags = params.tags.join(',');

  const response = await api.get<V4TasksResponse>('/v4/operations-tasks', queryParams);

  return {
    tasks: response.tasks.map(transformV4TaskListItem),
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
    pagination: {
      pageNumber: response.pageNumber,
      pageLength: response.pageLength,
      totalRecords: response.total,
      totalPages: response.pageLength > 0 ? Math.ceil(response.total / response.pageLength) : 0,
    },
  };
}

export async function fetchTask(id: string): Promise<Task> {
  const response = await api.get<V4Task>(`/v4/operations-tasks/${id}`);
  return transformV4Task(response);
}

/**
 * Compute dueDateFrom/dueDateTo from a preset, using America/Montreal timezone.
 */
function computeDueDateRange(preset: string): { dueDateFrom: string; dueDateTo: string } {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Montreal' }));
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  if (preset === 'today') {
    const today = fmt(new Date(year, month, day));
    return { dueDateFrom: today, dueDateTo: today };
  }

  if (preset === 'this_week') {
    const dayOfWeek = now.getDay(); // 0=Sun
    const monday = new Date(year, month, day - ((dayOfWeek + 6) % 7));
    const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
    return { dueDateFrom: fmt(monday), dueDateTo: fmt(sunday) };
  }

  // this_month
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  return { dueDateFrom: fmt(firstDay), dueDateTo: fmt(lastDay) };
}

export async function fetchKanbanTasks(params: KanbanQueryParams = {}): Promise<KanbanData> {
  const queryParams: Record<string, string> = {};

  if (params.projectId) queryParams.projectId = params.projectId;
  if (params.quoteId) queryParams.quoteId = params.quoteId;
  if (params.assignedToId) queryParams.assignedToId = params.assignedToId;
  if (params.priority) queryParams.priority = params.priority;
  if (params.search) queryParams.search = params.search;
  if (params.tags?.length) queryParams.tags = params.tags.join(',');
  if (params.statuses?.length) queryParams.statuses = params.statuses.join(',');
  if (params.dueDatePreset) {
    const { dueDateFrom, dueDateTo } = computeDueDateRange(params.dueDatePreset);
    queryParams.dueDateFrom = dueDateFrom;
    queryParams.dueDateTo = dueDateTo;
  }

  const response = await api.get<V4KanbanResponse>('/v4/operations-tasks/kanban', queryParams);
  return transformV4KanbanResponse(response);
}

export async function fetchTasksStats(
  params: { projectId?: string; quoteId?: string; assignedToId?: string } = {}
): Promise<TasksStats> {
  const queryParams: Record<string, string> = {};
  if (params.projectId) queryParams.projectId = params.projectId;
  if (params.quoteId) queryParams.quoteId = params.quoteId;
  if (params.assignedToId) queryParams.assignedToId = params.assignedToId;

  const response = await api.get<V4TasksStatsResponse>('/v4/operations-tasks/stats', queryParams);

  return {
    total_count: response.totalCount,
    by_status: response.byStatus as Record<TaskStatus, number>,
    by_priority: response.byPriority as Record<TaskPriority, number>,
    overdue_count: response.overdueCount,
    due_this_week: response.dueThisWeek,
    unassigned_count: response.unassignedCount,
  };
}

export async function fetchTasksByProject(
  projectId: string,
  params: { pageNumber?: number; pageLength?: number; status?: TaskStatus; includeSubtasks?: boolean } = {}
): Promise<TasksResponse> {
  const queryParams: Record<string, string | number | boolean> = {};
  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;
  if (params.status) queryParams.status = params.status;
  if (params.includeSubtasks) queryParams.includeSubtasks = true;

  const response = await api.get<V4TasksResponse>(
    `/v4/operations-tasks/by-project/${projectId}`,
    queryParams
  );

  return {
    tasks: response.tasks.map(transformV4TaskListItem),
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
    pagination: {
      pageNumber: response.pageNumber,
      pageLength: response.pageLength,
      totalRecords: response.total,
      totalPages: response.pageLength > 0 ? Math.ceil(response.total / response.pageLength) : 0,
    },
  };
}

export async function createTask(input: CreateTaskInput): Promise<Task> {
  const response = await api.post<V4Task>('/v4/operations-tasks', transformCreateInput(input));
  return transformV4Task(response);
}

export async function updateTask(id: string, input: UpdateTaskInput): Promise<Task> {
  const response = await api.put<V4Task>(
    `/v4/operations-tasks/${id}`,
    transformUpdateInput(input)
  );
  return transformV4Task(response);
}

export async function deleteTask(id: string): Promise<{ success: boolean }> {
  return api.delete(`/v4/operations-tasks/${id}`);
}

export async function updateTaskStatus(id: string, status: TaskStatus): Promise<Task> {
  const response = await api.patch<V4Task>(`/v4/operations-tasks/${id}/status`, { status });
  return transformV4Task(response);
}

export async function assignTask(
  id: string,
  assignee: {
    assigned_to_id?: string;
    assigned_to_name?: string;
    assigned_to_email?: string;
  }
): Promise<Task> {
  const response = await api.patch<V4Task>(`/v4/operations-tasks/${id}/assign`, {
    assignedToId: assignee.assigned_to_id,
    assignedToName: assignee.assigned_to_name,
    assignedToEmail: assignee.assigned_to_email,
  });
  return transformV4Task(response);
}

export async function updateTaskPositions(input: UpdatePositionsInput): Promise<{ success: boolean }> {
  return api.put('/v4/operations-tasks/positions', {
    updates: input.updates.map((u) => ({
      id: u.id,
      status: u.status,
      position: u.position,
    })),
  });
}

export async function addTaskComment(taskId: string, content: string): Promise<TaskComment> {
  const response = await api.post<V4TaskComment>(`/v4/operations-tasks/${taskId}/comments`, {
    content,
  });
  return transformV4TaskComment(response);
}

export async function deleteTaskComment(
  taskId: string,
  commentId: string
): Promise<{ success: boolean }> {
  return api.delete(`/v4/operations-tasks/${taskId}/comments/${commentId}`);
}

export async function fetchTaskAuditLogs(
  taskId: string,
  params: { pageNumber?: number; pageLength?: number } = {}
): Promise<{ logs: TaskAuditLog[] }> {
  const queryParams: Record<string, number> = {};
  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;

  const response = await api.get<V4AuditLogsResponse>(
    `/v4/operations-tasks/${taskId}/audit-logs`,
    queryParams
  );

  return {
    logs: response.logs.map((log) => ({
      id: log.id,
      task_id: log.taskId,
      action: log.action,
      changes: log.changes,
      actor_id: log.actorId,
      actor_name: log.actorName,
      actor_email: log.actorEmail,
      created_at: log.createdAt,
    })),
  };
}

// =============================================================================
// Related Data: Quotes, Projects & Assignees
// =============================================================================

export interface ProjectOption {
  id: string;
  project_number: string;
  project_name?: string;
  quote_number?: string;
  account_name?: string;
}

export interface QuoteOption {
  id: string;
  quote_number: string;
  project_name?: string;
  account_name?: string;
  status?: string;
}

export interface AssigneeOption {
  id: string;
  name: string;
  email?: string;
}

interface V4ProjectListItem {
  id: string;
  projectNumber: string;
  projectName?: string;
  quoteNumber?: string;
  accountName?: string;
  type: string;
  status: string;
}

interface V4ProjectsListResponse {
  projects: V4ProjectListItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4QuoteListItem {
  id: string;
  quoteNumber: string;
  projectName?: string;
  accountName?: string;
  status?: string;
}

interface V4QuotesListResponse {
  quotes: V4QuoteListItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V3User {
  _id: string;
  email: string;
  firstname: string;
  lastname: string;
  enabled: boolean;
  admin?: string;
}

interface V3UsersResponse {
  users: V3User[];
  pageNumber: number;
  pageLength: number;
}

/**
 * Fetch quotes for direct task linking.
 * Returns minimal quote data for dropdowns.
 */
export async function fetchQuoteOptions(): Promise<QuoteOption[]> {
  const response = await api.get<V4QuotesListResponse>('/v4/quotes', {
    pageLength: 100,
    sortKey: 'quoteNumber',
    sortDirection: -1,
  });

  return response.quotes.map((q) => ({
    id: q.id,
    quote_number: q.quoteNumber,
    project_name: q.projectName,
    account_name: q.accountName,
    status: q.status,
  }));
}

/**
 * Fetch operations projects for task linking
 * Returns minimal project data for dropdowns
 */
export async function fetchProjectOptions(): Promise<ProjectOption[]> {
  const response = await api.get<V4ProjectsListResponse>('/v4/operations-projects', {
    pageLength: 100,
    sortKey: 'project_number',
    sortDirection: -1,
  });

  return response.projects.map((p) => ({
    id: p.id,
    project_number: p.projectNumber,
    project_name: p.projectName,
    quote_number: p.quoteNumber,
    account_name: p.accountName,
  }));
}

/**
 * Fetch internal users for task assignment.
 * Returns ATEK employees + contractors from the PG users table.
 *
 * INTERNAL USE ONLY. This page is for ATEK staff managing internal operations.
 * The endpoint is org-scoped at the proxy (web_api_server/routes/v4/users.js):
 *   - PG super-admins (full_admin / director) see every user.
 *   - Non-super-admins in the ATEK Mongo org pass through unscoped.
 *   - Non-super-admins in any OTHER org get scoped to their own org's users —
 *     they will NOT see ATEK employees. Do not expose this page (or this
 *     fetch) to customer-facing flows. If a customer-facing assignee picker
 *     is ever needed, build a dedicated endpoint instead of relaxing the
 *     proxy scoping here.
 */
export async function fetchAssigneeOptions(): Promise<AssigneeOption[]> {
  // PG-backed: returns internal ATEK staff (employees + contractors) regardless
  // of legacy organisation membership. The v3 users endpoint we used before
  // filtered on `organisations[]` (often empty locally) and `admin_in` levels
  // that excluded 'standard user' — so the dropdown was silently empty.
  // dao/users-v4 returns `_id` mirroring mongo_user_id, which is the id shape
  // operations_tasks.assigned_to_id stores.
  const response = await api.get<V3UsersResponse>('/v4/users', {
    employment_type: 'employee,contractor',
    enabled: 'true',
    is_subaccount: 'false',
    pageLength: 100,
    sortKey: 'email',
    sortDirection: 1,
  });

  return (response.users || []).map((u) => ({
    id: u._id,
    name: `${u.firstname || ''} ${u.lastname || ''}`.trim() || u.email,
    email: u.email,
  }));
}

// =============================================================================
// Attachments
// =============================================================================

export interface Attachment {
  id: string;
  task_id: string;
  filename: string;
  original_filename: string;
  mime_type?: string;
  size_bytes?: number;
  storage_path: string;
  uploaded_by_id: string;
  uploaded_by_name: string;
  created_at: string;
}

interface V4Attachment {
  id: string;
  taskId: string;
  filename: string;
  originalFilename: string;
  mimeType?: string;
  sizeBytes?: number;
  storagePath: string;
  uploadedById: string;
  uploadedByName: string;
  createdAt: string;
}

interface V4AttachmentsResponse {
  attachments: V4Attachment[];
}

function transformV4Attachment(v4: V4Attachment): Attachment {
  return {
    id: v4.id,
    task_id: v4.taskId,
    filename: v4.filename,
    original_filename: v4.originalFilename,
    mime_type: v4.mimeType,
    size_bytes: v4.sizeBytes,
    storage_path: v4.storagePath,
    uploaded_by_id: v4.uploadedById,
    uploaded_by_name: v4.uploadedByName,
    created_at: v4.createdAt,
  };
}

/**
 * Fetch attachments for a task
 */
export async function fetchTaskAttachments(taskId: string): Promise<Attachment[]> {
  const response = await api.get<V4AttachmentsResponse>(
    `/v4/operations-tasks/${taskId}/attachments`
  );
  return response.attachments.map(transformV4Attachment);
}

/**
 * Upload a file attachment to a task
 */
export async function uploadTaskAttachment(taskId: string, file: File): Promise<Attachment> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.upload<V4Attachment>(
    `/v4/operations-tasks/${taskId}/attachments`,
    formData
  );
  return transformV4Attachment(response);
}

/**
 * Delete a task attachment
 */
export async function deleteTaskAttachment(
  taskId: string,
  attachmentId: string
): Promise<{ success: boolean }> {
  return api.delete(`/v4/operations-tasks/${taskId}/attachments/${attachmentId}`);
}

/**
 * Get the download URL for an attachment
 */
export function getAttachmentDownloadUrl(taskId: string, attachmentId: string): string {
  return `/api/v4/operations-tasks/${taskId}/attachments/${attachmentId}/download`;
}
